import { Calendar, ExternalLink, User, Video } from 'lucide-react';

import { toEmbedUrl } from './embed';

import type { Session } from '@/lib/api/lms';

function cleanTitle(t: string): string {
  return t.replace(/^Session:\s*/i, '');
}

function fmtDateTime(iso?: string): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function pickSession(sessions: Session[]): Session | null {
  return sessions.find((s) => toEmbedUrl(s.recording_url)) ?? sessions[0] ?? null;
}

export function VideoStage({ sessions }: { sessions: Session[] }) {
  const session = pickSession(sessions);
  if (!session) return null;

  const embed = toEmbedUrl(session.recording_url);
  const meta = [fmtDateTime(session.scheduled_at), session.trainer_name, session.duration_mins ? `${session.duration_mins} min` : null]
    .filter(Boolean).join(' · ');

  if (embed) {
    return (
      <section>
        <h2 className="mb-2 font-heading text-base font-bold">Recording</h2>
        <div className="relative w-full overflow-hidden rounded-xl border border-border bg-black" style={{ aspectRatio: '16 / 9' }}>
          <iframe
            src={embed}
            title={cleanTitle(session.title)}
            loading="lazy"
            allowFullScreen
            allow="accelerometer; clipboard-write; encrypted-media; picture-in-picture"
            sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
            className="absolute inset-0 h-full w-full"
          />
        </div>
        {meta && <p className="mt-2 text-xs text-muted-foreground">Recording · {meta}</p>}
      </section>
    );
  }

  const isPast = session.scheduled_at
    ? new Date(session.scheduled_at).getTime() < Date.now()
    : true;

  if (isPast) {
    return (
      <section className="rounded-xl border border-border bg-card p-4">
        <p className="mt-1 font-heading text-base font-bold text-foreground">{cleanTitle(session.title)}</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          {session.scheduled_at && <span className="inline-flex items-center gap-1"><Calendar className="h-4 w-4" />{fmtDateTime(session.scheduled_at)}</span>}
          {session.trainer_name && <span className="inline-flex items-center gap-1"><User className="h-4 w-4" />{session.trainer_name}</span>}
          {session.duration_mins && <span>{session.duration_mins} min</span>}
        </div>
        <p className="mt-2 text-sm text-muted-foreground">Recording coming soon</p>
        {session.recording_url && (
          <div className="mt-3">
            <a href={session.recording_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted/50">
              <ExternalLink className="h-4 w-4" /> Watch recording
            </a>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-primary/30 bg-primary/5 p-4">
      <p className="text-[11px] font-bold uppercase tracking-wide text-primary">Live session</p>
      <p className="mt-1 font-heading text-base font-bold text-foreground">{cleanTitle(session.title)}</p>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
        {session.scheduled_at && <span className="inline-flex items-center gap-1"><Calendar className="h-4 w-4" />{fmtDateTime(session.scheduled_at)}</span>}
        {session.trainer_name && <span className="inline-flex items-center gap-1"><User className="h-4 w-4" />{session.trainer_name}</span>}
        {session.duration_mins && <span>{session.duration_mins} min</span>}
      </div>
      <div className="mt-3 flex gap-2">
        {session.meeting_url && (
          <a href={session.meeting_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90">
            <Video className="h-4 w-4" /> Join session
          </a>
        )}
        {session.recording_url && (
          <a href={session.recording_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted/50">
            <ExternalLink className="h-4 w-4" /> Watch recording
          </a>
        )}
      </div>
    </section>
  );
}
